import { Box, Typography } from "@mui/material";
import DriverContactDialog from "../../shared/CustomDialog";
import { useRTL } from "hooks/isRTL";

/**
 * FlashingCardBody Component
 *
 * Displays the body section of a flashing card, including sender, receiver, cycle time,
 * scheduled and actual times, driver information, and the reason.
 * It also allows interaction through a driver contact dialog.
 *
 * @component
 * @param {Object} props  The component props.
 * @param {string} props.senderName  Name of the sender.
 * @param {string} props.receiverName  Name of the receiver.
 * @param {string} props.cycleTime  Elapsed time since the issue was created.
 * @param {string} props.scheduledTime  Scheduled time for the order.
 * @param {string} props.actualTime  Actual time for the order.
 * @param {string} props.driverName  Name of the driver.
 * @param {string} props.reason  Reason for the issue.
 * @param {boolean} props.dialogOpen  State indicating if the driver contact dialog is open.
 * @param {Function} props.handleDialogOpen  Function to open the driver contact dialog.
 * @param {Function} props.handleDialogClose  Function to close the driver contact dialog.
 * @param {string} props.driverPhoneNumber  Phone number of the driver.
 *
 * @returns {JSX.Element} The rendered component.
 */
function FlashingCardBody({
  senderName,
  receiverName,
  cycleTime,
  scheduledTime,
  actualTime,
  driverName,
  reason,
  dialogOpen,
  handleDialogOpen,
  handleDialogClose,
  driverPhoneNumber,
}) {
  const { t } = useRTL();
  return (
    <Box
      sx={{
        color: "text.solidGray",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        fontWeight: "500",
      }}
    >
      <Box>
        {t("senderName")}:{" "}
        <Typography
          component="span"
          sx={{ fontWeight: "bold", color: "text.black" }}
        >
          {t(senderName)}
        </Typography>
      </Box>

      <Box>
        {t("receiverName")}:{" "}
        <Typography
          component="span"
          sx={{ fontWeight: "bold", color: "text.black" }}
        >
          {receiverName}
        </Typography>
      </Box>

      <Box>
        {t("cycleTime")}:{" "}
        <Typography
          component="span"
          sx={{ fontWeight: "bold", color: "text.black" }}
        >
          {cycleTime}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 500,
        }}
      >
        <span>
          {t("scheduledTime")}:{" "}
          <Typography
            component="span"
            sx={{ fontWeight: "bold", color: "text.black" }}
          >
            {scheduledTime}
          </Typography>
        </span>
        <span>
          {t("actualTime")}:{" "}
          <Typography
            component="span"
            sx={{ fontWeight: "bold", color: "text.black" }}
          >
            {actualTime}
          </Typography>
        </span>
      </Box>

      <Box>
        <DriverContactDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          content={[
            { label: t("name"), value: driverName },
            { label: t("phoneNumber"), value: driverPhoneNumber },
            // { label: t("location"), value: "Location" }, //mark
          ]}
          title={t("driverContactInformation")}
        />
        {t("driverName")}:{" "}
        <Typography
          onClick={handleDialogOpen}
          component="span"
          sx={{
            fontWeight: "bold",
            textDecoration: "underline",
            color: "text.black",
            cursor: "pointer",
          }}
        >
          {driverName}
        </Typography>
      </Box>

      <Box>
        {t("reason")}:{" "}
        <Typography
          component="span"
          sx={{ fontWeight: "bold", color: "text.black" }}
        >
          {reason}
        </Typography>
      </Box>
    </Box>
  );
}

export default FlashingCardBody;
